// AiPersonaGenerator.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../services/api';
import { isPending } from '../../services/apiUtils.js';
import { FREE_RESEARCH_LIMITS } from '../../payment/PricingUtils';
import { 
  FILTER_CONFIG,
  createInitialFilters,
  createEmptyFilters,
} from './filterConfig';

const AiPersonaGenerator = ({
  userData,
  setUserData,
  onTopicUpdate,
  setError,
  setWarningMessage,
  setOpenWarningSnackbar,
  maxPersonas,
  isAdmin,
  hasActiveSubscription,
  setCurrentPage,
  setSelectedAttribute,
}) => {
  const [loading, setLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [mainAccordionExpanded, setMainAccordionExpanded] = useState(false);
  const hasResponses = userData?.surveyResponses && userData.surveyResponses.length > 0;

  const isFreeResearch = !isAdmin && !hasActiveSubscription;

  const [filters, setFilters] = useState({
    numberOfPersonas: Math.min(20, maxPersonas),
    ...Object.values(FILTER_CONFIG).reduce((acc, filter) => {
      acc[filter.key] = filter.defaultValue;
      return acc;
    }, {})
  });

  useEffect(() => {
    const loadInitialFilters = async () => {
      if (userData && Object.keys(userData).length > 0) {
        try {
          const initialFilters = await createInitialFilters(userData, maxPersonas);
          setFilters(initialFilters);
        } catch (error) {
          console.error('Error loading initial filters:', error);
        }
      }
    };

    loadInitialFilters();
  }, [userData, maxPersonas]);

  // Check if numberOfPersonas exceeds limits for free research and adjust if needed
  useEffect(() => {
    console.log("IN USE EFFECT, filters is", filters);
    if (isFreeResearch && filters.numberOfPersonas > FREE_RESEARCH_LIMITS.MAX_PERSONAS) {
      setFilters(prev => ({
        ...prev,
        numberOfPersonas: FREE_RESEARCH_LIMITS.MAX_PERSONAS
      }));
      setError(`Free research is limited to ${FREE_RESEARCH_LIMITS.MAX_PERSONAS} personas.`);
    }
  }, [filters.numberOfPersonas, isFreeResearch]);

  useEffect(() => {
    if (filters.numberOfPersonas > maxPersonas) {
      setFilters(prev => ({
        ...prev,
        numberOfPersonas: maxPersonas
      }));
      setError(`You are limited to ${maxPersonas} personas based on your current plan.`);
    }
  }, [filters.numberOfPersonas, maxPersonas]);

  const handleCategoryExpand = (key) => (event, isExpanded) => {
    setExpandedCategories(prev => ({
      ...prev,
      [key]: isExpanded
    }));
  };

  const handleClearFilters = () => {
    setFilters(createEmptyFilters());
  };

  const handleCheckboxChange = (filterKey, value) => {
    setFilters((prev) => {
      const currentValues = prev[filterKey];
      if (currentValues.includes(value)) {
        return { ...prev, [filterKey]: currentValues.filter((item) => item !== value) };
      }
      return { ...prev, [filterKey]: [...currentValues, value] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (filters.numberOfPersonas > maxPersonas) {
      let errorMessage = '';
      if (isAdmin) {
        errorMessage = `Admin accounts are limited to ${maxPersonas} personas per research.`;
      } else if (hasActiveSubscription) {
        errorMessage = `Your subscription plan is limited to ${maxPersonas} personas per research.`;
      } else {
        errorMessage = `Free research is limited to ${maxPersonas} personas. Please reduce the number of personas or upgrade to a subscription plan.`;
      }
      
      setError(errorMessage);
      return;
    }

    if (isPending(userData, setWarningMessage, setOpenWarningSnackbar)) {
      setLoading(false);
      return;
    }

    if (hasResponses) {
      const proceed = window.confirm(
        'After re-generating personas, you will have to re-generate responses. Do you want to proceed?'
      );
      if (!proceed) {
        return;
      }
    } 

    setLoading(true);

    try {
      let personas;
      try {
        personas = await api.selectPersonas({
          userID: userData.userID,
          researchID: userData.researchID,
          filters: filters
        });
      } catch (err) {
        console.log("Error when selecting personas", err);
        setError("Error on the server, please try again later or contact us if this issue keeps reoccurring");
        setLoading(false);
        return;
      }

      if (personas && Object.keys(personas).length >= filters.numberOfPersonas) {
        const newUserData = {...userData, selectedPersonas: personas, personaFilters: { ...filters }, personasStatus: 'COMPLETED', lastModified: Date.now() / 1000, responsesStatus: 'NOT_STARTED', surveyResponses: null, importedPersonas: false}
        setUserData(newUserData);

        if (onTopicUpdate) {
          console.log("IN HANDLE SUBMIT, lets update topic");
          onTopicUpdate(newUserData);
        }

        setCurrentPage(1);

        // Simply select the first attribute TODO: this can be done better in the future, making it consistent with CsvUploader
        const firstNonNameProperty = Object.keys(Object.values(personas)[0])
        .filter(key => key !== "name")[0];
    
        setSelectedAttribute(firstNonNameProperty);

      } else {
        setError("Not enough valid personas could be created with the given filters. Please adjust your filters and try again.");
      }
    } catch (err) {
      setError("An error occurred after creating personas, please try again later or contact us if this issue keeps reoccurring");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Number of Personas */}
      <TextField
        fullWidth
        type="number"
        label="Number of Personas"
        value={filters.numberOfPersonas}
        onChange={(e) => {
          const value = parseInt(e.target.value, 10);
          if (value > maxPersonas) {
            let errorMessage = `You are limited to ${maxPersonas} personas based on your current plan.`;
            setError(errorMessage);
            return;
          }
          setFilters(prev => ({ ...prev, numberOfPersonas: value }));
          setError(null);
        }}
        InputProps={{
          inputProps: { min: 1, max: maxPersonas }
        }}
        margin="normal"
        helperText={`Maximum ${maxPersonas} personas for your plan`}
      />
      
      {/* Demographic Filters Accordion */}
      <Accordion sx={{ mt: 2 }}
      expanded={mainAccordionExpanded}
      onChange={(_, isExpanded) => setMainAccordionExpanded(isExpanded)}
      >
        <Tooltip 
            title="Configure filters to specify the demographic characteristics of your personas"
            placement="top"
            arrow
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="bold">Click here to set your filters</Typography>
            </AccordionSummary>
        </Tooltip>
        <AccordionDetails>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontSize: 12 }}>
              When a demographic is not checked, personas will be selected from all available options
            </Typography>
          </Box>
          {Object.values(FILTER_CONFIG).map(({ key, label, options }) => {
            const isCategorySelected = filters[key].length > 0;
            
            return (
              <Accordion 
                key={key}
                expanded={!!expandedCategories[key]}
                onChange={handleCategoryExpand(key)}
                sx={{ mb: 1 }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isCategorySelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({
                              ...prev,
                              [key]: [...options]
                            }));
                          } else {
                            setFilters(prev => ({
                              ...prev,
                              [key]: []
                            }));
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={label}
                    onClick={(e) => e.stopPropagation()}
                  />
                </AccordionSummary>
                <AccordionDetails
                  sx={{ mb: 0 }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormGroup>
                      <Grid container spacing={1}>
                        {options.map((option) => (
                          <Grid item xs={6} key={option}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={filters[key].includes(option)}
                                  onChange={() => handleCheckboxChange(key, option)}
                                />
                              }
                              label={option}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </FormGroup>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            [key]: []
                          }));
                        }}
                        sx={{ mr: 1, fontSize: '0.7rem', py: 0.5 }}
                      >
                        Clear
                      </Button>
                      <Button 
                        size="small" 
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            [key]: [...options]
                          }));
                        }}
                        sx={{ fontSize: '0.7rem', py: 0.5 }}
                      >
                        Select All
                      </Button>
                    </Box>
                  </FormControl>
                </AccordionDetails>
              </Accordion>
            );
          })}
            
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" onClick={handleClearFilters} sx={{ mr: 2 }}>
              Clear All Filters
            </Button>
            <Button variant="contained" onClick={() => {
              setExpandedCategories({});  // Close all accordions
              setMainAccordionExpanded(false);  // Close main accordion
            }}>
              Save Filters
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        sx={{ mt: 2, mr: 2 }}
      >
        {loading ? <CircularProgress size={24} /> : (userData?.selectedPersonas ? 'Regenerate Personas' : 'Generate Personas')}
      </Button>
    </div>
  );
};

export default AiPersonaGenerator;
